import { Button, Card, Modal, List } from "antd";
import Meta from "antd/es/card/Meta";

import { useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import { LinkOutlined } from "@ant-design/icons";
import { FaCheck } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";

import icon1 from "../../../../assets/icons/insurance_addons_icons/01.svg";
import icon2 from "../../../../assets/icons/insurance_addons_icons/02.svg";
import icon3 from "../../../../assets/icons/insurance_addons_icons/03.svg";
import icon4 from "../../../../assets/icons/insurance_addons_icons/04.svg";

const providerLabel = (provider, t) => {
    if (provider === "teqMobility") return t("createAddons.teqMobility");
    if (provider === "bonzah") return t("createAddons.bonzah");
    if (provider === "abi") return t("createAddons.abi");
    return provider || "—";
};

const coverageLabel = (value, t) => {
    const key = `createAddons.${value}`;
    const label = t(key);
    return label === key ? value : label;
};

const InsuranceCard = ({ addon, handleModalState }) => {
    const { t } = useTranslation(["addons"]);
    const cardRef = useRef(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    return (
        <div className="relative group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="h-full">
                <Card
                    ref={cardRef}
                    className="h-full flex flex-col"
                    styles={{
                        body: { flex: 1, display: "flex", flexDirection: "column" },
                    }}>
                    <div className="flex-1 flex flex-col">
                        <Meta
                            title={<h1 className="text-center mt-6">{addon.name}</h1>}
                            description={
                                <div className="flex flex-col space-y-6 mt-6 flex-1">
                                    {/* Fixed height icon container that won't shrink with content */}
                                    <div className="flex items-center justify-center gap-4 min-h-[32px] flex-shrink-0">
                                        {/* Fixed size icons with min-width and min-height to prevent shrinking */}
                                        <img
                                            src={icon1}
                                            className="h-8 w-8 min-h-[32px] min-w-[32px] object-contain"
                                            alt="Insurance Icon 1"
                                        />
                                        <img
                                            src={icon2}
                                            className="h-8 w-8 min-h-[32px] min-w-[32px] object-contain"
                                            alt="Insurance Icon 2"
                                        />
                                        <img
                                            src={icon3}
                                            className="h-8 w-8 min-h-[32px] min-w-[32px] object-contain"
                                            alt="Insurance Icon 3"
                                        />
                                        <img
                                            src={icon4}
                                            className="h-8 w-8 min-h-[32px] min-w-[32px] object-contain"
                                            alt="Insurance Icon 4"
                                        />
                                    </div>
                                    {/* Provider links badge */}
                                    {addon?.providerLinks &&
                                        typeof addon.providerLinks === "object" &&
                                        Object.keys(addon.providerLinks).length > 0 && (
                                            <div className="flex justify-center">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                                    <LinkOutlined /> {t("createAddons.providerLinks")}
                                                </span>
                                            </div>
                                        )}
                                    {/* Feature list with text wrapping and proper alignment */}
                                    <div className="flex-1 overflow-y-auto">
                                        {addon.featureList?.map((feature, index) => (
                                            <div key={index} className="flex items-start gap-2 mb-2">
                                                <div className="flex-shrink-0 mt-1">
                                                    {feature.isActive ? (
                                                        <FaCheck color="green" />
                                                    ) : (
                                                        <FaTimes color="red" />
                                                    )}
                                                </div>
                                                <p className="flex-1 break-words">{feature.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            }
                        />
                    </div>

                    {addon.status === "active" ? (
                        <div className="absolute top-3 right-3">
                            <div className="flex justify-center items-center w-12 rounded-3xl bg-green-400">
                                {t("createAddons.switchOn")}
                            </div>
                        </div>
                    ) : (
                        <div className="absolute top-3 right-3">
                            <div className="flex justify-center items-center w-14 rounded-3xl bg-gray-300">
                                {t("createAddons.switchOff")}
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-100 px-4 pb-4 pt-3 lg:hidden">
                <Button type="primary" shape="round" className="!h-10 w-full" onClick={() => setViewModalOpen(true)}>
                    {t("view")}
                </Button>
                <Button
                    shape="round"
                    className="!h-10 w-full"
                    onClick={() => handleModalState({ isOpen: true, addonToEdit: addon })}>
                    {t("editAddon")}
                </Button>
            </div>
            <div className="absolute inset-0 hidden flex-col items-center justify-center gap-2 rounded-md bg-black bg-opacity-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:flex">
                <Button type="primary" shape="round" onClick={() => setViewModalOpen(true)}>
                    {t("view")}
                </Button>
                <Button shape="round" onClick={() => handleModalState({ isOpen: true, addonToEdit: addon })}>
                    {t("editAddon")}
                </Button>
            </div>

            <Modal
                title={addon?.name}
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setViewModalOpen(false)}>
                        {t("cancel")}
                    </Button>,
                    <Button
                        key="edit"
                        type="primary"
                        onClick={() => {
                            setViewModalOpen(false);
                            handleModalState({ isOpen: true, addonToEdit: addon });
                        }}>
                        {t("editAddon")}
                    </Button>,
                ]}
                centered
                width={{
                    xs: "calc(100vw - 2rem)",
                    sm: 600,
                }}>
                <div className="max-h-[min(90vh,720px)] overflow-y-auto rounded-2xl p-4 sm:min-h-[320px] sm:p-6">
                    <List
                        dataSource={[
                            {
                                label: t("name"),
                                value: addon?.name ?? "—",
                            },
                            {
                                label: t("createAddons.provider"),
                                value: providerLabel(addon?.provider, t),
                            },
                            {
                                label: t("createAddons.insuranceCategory"),
                                value: addon?.insuranceCategory ? coverageLabel(addon.insuranceCategory, t) : "—",
                            },
                            {
                                label: t("createAddons.coverageType"),
                                value: addon?.coverageType ? coverageLabel(addon.coverageType, t) : "—",
                            },
                            {
                                label: "Status",
                                value: (
                                    <span
                                        className={`inline-flex px-2 py-1 rounded text-sm font-medium ${
                                            addon?.status === "active"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-100 text-gray-700"
                                        }`}>
                                        {addon?.status === "active"
                                            ? t("createAddons.switchOn")
                                            : t("createAddons.switchOff")}
                                    </span>
                                ),
                            },
                        ]}
                        renderItem={(item) => (
                            <List.Item>
                                <div className="w-full">
                                    <span className="text-gray-500 text-sm block mb-1">{item.label}</span>
                                    <div className="font-medium">{item.value}</div>
                                </div>
                            </List.Item>
                        )}
                    />
                    {addon?.providerLinks &&
                        typeof addon.providerLinks === "object" &&
                        Object.keys(addon.providerLinks).length > 0 && (
                            <div className="mt-4">
                                <span className="text-gray-500 text-xs block mb-2">
                                    {t("createAddons.providerLinks")}
                                </span>
                                <div className="flex flex-col gap-1 text-sm">
                                    {addon.providerLinks.excludedVehicles && (
                                        <a
                                            href={addon.providerLinks.excludedVehicles}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                                            <LinkOutlined className="text-xs" />
                                            {t("createAddons.excludedVehicles")}
                                        </a>
                                    )}
                                    {addon.providerLinks.terms && (
                                        <a
                                            href={addon.providerLinks.terms}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                                            <LinkOutlined className="text-xs" />
                                            {t("createAddons.terms")}
                                        </a>
                                    )}
                                    {addon.providerLinks.privacy && (
                                        <a
                                            href={addon.providerLinks.privacy}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                                            <LinkOutlined className="text-xs" />
                                            {t("createAddons.privacy")}
                                        </a>
                                    )}{addon.providerLinks.cdwPdF && (
                                        <a
                                            href={addon.providerLinks.cdwPdF}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                                            <LinkOutlined className="text-xs" />
                                            {t("createAddons.cdwPdf")}
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    {addon?.pricingRanges?.length > 0 && (
                        <div className="mt-4">
                            <span className="text-gray-500 text-sm block mb-3 font-medium">
                                {t("createAddons.pricingRanges")}
                            </span>
                            <List
                                dataSource={addon.pricingRanges}
                                split={false}
                                renderItem={(range) => {
                                    const minCarValue = range?.minCarValue ?? 0;
                                    const maxCarValue = range?.maxCarValue ?? null;
                                    const baseprice = range?.baseprice ?? 0;
                                    const upchargeAmount = range?.upchargeAmount ?? 0;
                                    const upchargeAllowed = range?.upchargeAllowed ?? true;

                                    return (
                                        <List.Item>
                                            <div className="w-full border rounded p-3 bg-gray-50">
                                                <div className="mb-2">
                                                    <span className="text-xs text-gray-600">
                                                        {t("createAddons.carValueRange")}:
                                                    </span>
                                                    <span className="ml-2 text-sm font-medium">
                                                        ${minCarValue.toLocaleString()} {t("createAddons.to")}{" "}
                                                        {maxCarValue != null
                                                            ? `$${maxCarValue.toLocaleString()}`
                                                            : t("createAddons.andAbove")}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <span className="text-xs text-gray-500 block">
                                                            {t("createAddons.basePrice")}
                                                        </span>
                                                        <p className="font-medium">${baseprice}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-500 block">
                                                            {t("createAddons.upchargeAmount")}
                                                        </span>
                                                        <p className="font-medium">
                                                            ${upchargeAmount}
                                                            {!upchargeAllowed && (
                                                                <span className="text-xs text-gray-400 ml-1">
                                                                    ({t("createAddons.informationOnly")})
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        </div>
                    )}
                    {addon?.featureList?.length > 0 && (
                        <div className="mt-4">
                            <span className="text-gray-500 text-sm block mb-2 font-medium">Features</span>
                            <List
                                dataSource={addon.featureList}
                                renderItem={(feature) => (
                                    <List.Item>
                                        <div className="flex items-start gap-2 w-full">
                                            {feature.isActive ? (
                                                <FaCheck className="text-green-600 mt-0.5 flex-shrink-0" />
                                            ) : (
                                                <FaTimes className="text-red-500 mt-0.5 flex-shrink-0" />
                                            )}
                                            <span>{feature.text || "—"}</span>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

InsuranceCard.propTypes = {
    addon: PropTypes.object.isRequired,
    handleModalState: PropTypes.func.isRequired,
};

export default InsuranceCard;
